import {ErrorHandler, DEFAULT_ERROR} from '../modules/error_handler';
import Logger from '../util/logger_utils';
import HttpUtils from '../util/http_utils';
import LanguageManager from '../modules/language_manager/language_manager';
import async from 'async';
import config from '../config';
// require('script!../../dist/bitmovinplayer.min.js');

const BITMOVIN_LICENSE_KEY = '9c89f7d3-2fe7-4986-aaaf-dacaf8f37b93';

/**
 * Implements the StarzPlayer API to make it work with Bitmovin player
 * @constructor
 */
function BitmovinAdapter() {
    this.adapterName = 'Bitmovin';
    this.player = bitmovin.player(config.playerElementId);
    this.play = this.player.play;
    this.pause = this.player.pause;
    this.seek = this.player.seek;
    this.getName = () => 'Bitmovin';
    this.getVersion = this.player.getVersion;
    this.getCurrentTime = this.player.getCurrentTime;
    this.getTotalStalledTime = this.player.getTotalStalledTime;
    this.destroy = () => {
        try {
            this.player.destroy();
        } catch (e) {
            this.pause();
            document.getElementById('starzplayer').innerHTML = '';
        }
    };
    this.exitFullscreen = this.player.exitFullscreen;
    this.enterFullscreen = this.player.enterFullscreen;
    this.unload = this.player.unload;

    // Subtitle and Audio related controls
    this.getAvailableSubtitles = this.player.getAvailableSubtitles;
    this.setSubtitle = this.player.setSubtitle;
    this.getSubtitle = this.player.getSubtitle;
    this.getAvailableAudio = this.player.getAvailableAudio;
    this.setAudio = this.player.setAudio;
    this.getAudio = this.player.getAudio;
    
    this.getAvailableVideoQualities = this.player.getAvailableVideoQualities;
    this.getPlaybackVideoData = this.player.getPlaybackVideoData;
    this.getVideoQuality = this.player.getVideoQuality;
    this.setVideoQuality = this.player.setVideoQuality;

    this.isPlaying = this.player.isPlaying;
    this.getDuration = this.player.getDuration;

    // Volume control functions
    this.setVolume = this.player.setVolume;
    this.getVolume = this.player.getVolume;
    this.unmute = this.player.unmute;
    this.mute = this.player.mute;
    this.isMuted = this.player.isMuted;
    
    /*
     +    Possible actions:
     +     Methods
     +     addEventHandler
     +     addMetadata
     +     addSubtitle
     +     castStop
     +     castVideo
     +     castVideo
     +     clearQueryParameters
     +     destroy
     +     enterFullscreen
     +     exitFullscreen
     +     getAudio
     +     getAudioBufferLength
     +     getAvailableAudio
     +     getAvailableAudioQualities
     +     getAvailableImpressionServers
     +     getAvailableLicenseServers
     +     getAvailableSubtitles
     +     getAvailableVideoQualities
     +     getConfig
     +     getCurrentTime
     +     getDownloadedAudioData
     +     getDownloadedVideoData
     +     getDroppedFrames
     +     getDuration
     +     getFigure
     +     getManifest
     +     getMaxTimeShift
     +     getPlaybackAudioData
     +     getPlaybackSpeed
     +     getPlaybackVideoData
     +     getPlayerType
     +     getSnapshot
     +     getStreamType
     +     getSubtitle
     +     getSupportedDRM
     +     getSupportedTech
     +     getThumb
     +     getTimeShift
     +     getTotalStalledTime
     +     getVersion
     +     getVideoBufferLength
     +     getVolume
     +     getVRStatus
     +     hasEnded
     +     isAd
     +     isCastAvailable
     +     isCasting
     +     isDRMSupported
     +     isFullscreen
     +     isLive
     +     isMuted
     +     isPaused
     +     isPlaying
     +     isReady
     +     isSetup
     +     isStalled
     +     load
     +     mute
     +     pause
     +     play
     +     removeEventHandler
     +     removeSubtitle
     +     scheduleAd
     +     seek
     +     setAudio
     +     setAudioQuality
     +     setAuthentication
     +     setLastSegment
     +     setPlaybackSpeed
     +     setPosterImage
     +     setQueryParameters
     +     setSkin
     +     setSubtitle
     +     setup
     +     setVideoElement
     +     setVideoQuality
     +     setVolume
     +     setVRStereo
     +     skipAd
     +     timeShift
     +     unload
     +     unmute
     */
}

/**
 * Map the bitmovin error code to a starzplayer error
 * @param {Number} errorCode - The bitmovin error code
 */
BitmovinAdapter.prototype.mapErrorCode = function(errorCode) {
    const errors = {
        1023: {
            message: LanguageManager.get('error_571101_no_asset_type'),  // No stream found for supported technologies",
            code: 571101
        },
        1022: {
            message: LanguageManager.get('error_571107_generic'), // Browser does not support important array objects,
            code: 571107
        },
        1021: {
            message: LanguageManager.get('error_571107_generic'), // Player technology not compatible with VR playback,,
            code: 571107
        },
        1020: {
            message: LanguageManager.get('error_571107_generic'), // No supported VR source was found,,
            code: 571107
        },
        1019: {
            message: LanguageManager.get('error_571108_not_authenticated'), // Invalid impression server url,,
            code: 571108
        },
        1018: {
            message: LanguageManager.get('error_571108_not_authenticated'), // "Invalid license server url,,
            code: 571108
        },
        3031: {
            message: LanguageManager.get('error_571108_not_authenticated'), // DRM: Could not parse Fairplay certificate,,
            code: 571108
        },
        3030: {
            message: LanguageManager.get('error_571107_generic'), // Could not skip gap,,
            code: 571107
        },
        3029: {
            message: LanguageManager.get('error_571121_chunks_unavailable'), // Native HLS stream error,,
            code: 571121
        },
        3028: {
            message: LanguageManager.get('error_571121_chunks_unavailable'), // Progressive stream error,,
            code: 571121
        },
        3027: {
            message: LanguageManager.get('error_571101_no_asset_type'), // DRM certificate error,,
            code: 571101
        },
        3026: {
            message: LanguageManager.get('error_571121_chunks_unavailable'), // Progressive stream timeout,,
            code: 571121
        },
        3025: {
            message: LanguageManager.get('error_571121_chunks_unavailable'), // Segment download timeout,,
            code: 571121
        },
        3024: {
            message: LanguageManager.get('error_571120_manifest_unavailable'), // Manifest download timeout,,
            code: 571120
        },
        3023: {
            message: LanguageManager.get('error_571199_connection_error'), // Network error,,
            code: 571199
        },
        3022: {
            message: LanguageManager.get('error_571120_manifest_unavailable'), // Manifest error,,
            code: 571120
        },
        3021: {
            message: LanguageManager.get('error_571101_no_asset_type'), // DRM system not supported,,
            code: 571101
        },
        3020: {
            message: LanguageManager.get('error_571101_no_asset_type'), // DRM key error,,
            code: 571101
        },
        3019: {
            message: LanguageManager.get('error_571101_no_asset_type'), // DRM certificate requested failed,,
            code: 571101
        },
        3018: {
            message: LanguageManager.get('error_571122_player_error'), // Could not create MediaKeys,,
            code: 571122
        },
        3017: {
            message: LanguageManager.get('error_571122_player_error'), // Could not create key session,,
            code: 571122
        },
        3016: {
            message: LanguageManager.get('error_571122_player_error'), // Could not create key system,,
            code: 571122
        },
        3015: {
            message: LanguageManager.get('error_571107_generic'), // Unsupported codec or file format,,
            code: 571107
        },
        3014: {
            message: LanguageManager.get('error_571107_generic'), // Key size not supported,,
            code: 571107
        },
        3013: {
            message: LanguageManager.get('error_571101_no_asset_type'), // Decryption Key or KeyID missing,,
            code: 571101
        },
        3012: {
            message: LanguageManager.get('error_571101_no_asset_type'), // Invalid header pair for DRM,,
            code: 571101
        },
        3011: {
            message: LanguageManager.get('error_571114_session_expired'), // DRM license request failed,,
            code: 571114
        },
        3010: {
            message: LanguageManager.get('error_571122_player_error'), // Error synchronizing streams,,
            code: 571122
        },
        3009: {
            message: LanguageManager.get('error_571107_generic'), // Maximum retries exceeded,,
            code: 571107
        },
        3007: {
            message: LanguageManager.get('error_571107_generic'), // Subitles or captions can not be loaded,,
            code: 571107
        },
        3006: {
            message: LanguageManager.get('error_571120_manifest_unavailable'), // Manifest can not be loaded,,
            code: 571120
        },
        3005: {
            message: LanguageManager.get('error_571120_manifest_unavailable'), // No manifest URL,,
            code: 571120
        },
        3004: {
            message: LanguageManager.get('error_571121_chunks_unavailable'), // Could not find segment URL,,
            code: 571121
        },
        3003: {
            message: LanguageManager.get('error_571107_generic'), // Unsupported TFDT box version,,
            code: 571107
        },
        3002: {
            message: LanguageManager.get('error_571121_chunks_unavailable'), // Segment contains no data,,
            code: 571121
        },
        3001: {
            message: LanguageManager.get('error_571120_manifest_unavailable'), // Unsupported manifest format,,
            code: 571120
        },
        3000: {
            message: LanguageManager.get('error_571107_generic'), // Unknown HTML5 error,,
            code: 571107
        },
        900: {
            message: LanguageManager.get('error_571107_generic'), // Undefined VAST error,,
            code: 571107
        },
        403: {
            message: LanguageManager.get('error_571107_generic'), // No supported VAST media file found,,
            code: 571107
        },
        303: {
            message: LanguageManager.get('error_571107_generic'), // No VAST response,,
            code: 571107
        },
        200: {
            message: LanguageManager.get('error_571107_generic'), // VAST trafficking error,,
            code: 571107
        },
        2015: {
            message: LanguageManager.get('error_571101_no_asset_type'), // Unsupported codec or file format,,
            code: 571101
        },
        2008: {
            message: LanguageManager.get('error_571107_generic'), // Adobe Access DRM Error,,
            code: 571107
        },
        2007: {
            message: LanguageManager.get('error_571121_chunks_unavailable'), // Segment can not be loaded,,
            code: 571121
        },
        2006: {
            message: LanguageManager.get('error_571120_manifest_unavailable'), // Manifest can not be loaded,,
            code: 571120
        },
        2001: {
            message: LanguageManager.get('error_571106_authentication'), // DRM Error occured,,
            code: 571106
        },
        2000: {
            message: LanguageManager.get('error_571107_generic'), // Unknown flash error,,
            code: 571107
        },
        1017: {
            message: LanguageManager.get('error_571106_authentication'), // License not compatible with domain,,
            code: 571106
        },
        1016: {
            message: LanguageManager.get('error_571106_authentication'), // License error,,
            code: 571106
        },
        1015: {
            message: LanguageManager.get('error_571122_player_error'), // Forced player is not supported,,
            code: 571122
        },
        1014: {
            message: LanguageManager.get('error_571101_no_asset_type'), // Player type is unknown,,
            code: 571101
        },
        1013: {
            message: LanguageManager.get('error_571101_no_asset_type'), // Stream type is not supported,,
            code: 571101
        },
        1012: {
            message: LanguageManager.get('error_571107_generic'), // Player files can not be loaded,,
            code: 571107
        },
        1011: {
            message: LanguageManager.get('error_571107_generic'), // No valid configuration object,,
            code: 571107
        },
        1010: {
            message: LanguageManager.get('error_571101_no_asset_type'), // Unsupported protocol,,
            code: 571101
        },
        1009: {
            message: LanguageManager.get('error_571107_generic'), // Skin can not be loaded,,
            code: 571107
        },
        1008: {
            message: LanguageManager.get('error_571107_generic'), // Domain error,,
            code: 571107
        },
        1007: {
            message: LanguageManager.get('error_571107_generic'), // Flash player version not supported,,
            code: 571107
        },
        1006: {
            message: LanguageManager.get('error_571101_no_asset_type'), // No supported technology was detected,,
            code: 571101
        },
        1005: {
            message: LanguageManager.get('error_571107_generic'), // Flash creation error,,
            code: 571107
        },
        1004: {
            message: LanguageManager.get('error_571107_generic'), // HTML video element error,,
            code: 571107
        },
        1003: {
            message: LanguageManager.get('error_571101_no_asset_type'), // No stream provided,,
            code: 571101
        },
        1002: {
            message: LanguageManager.get('error_571107_generic'), // Key error,,
            code: 571107
        },
        1000: {
            message: LanguageManager.get('error_571107_generic'), // Unknown error,
            code: 571107
        }
    };

    if (!errors[errorCode]) {
        Logger.warn('Could not find errormessage for errorCode: ' + errorCode + '. Returning generic error.');
        return LanguageManager.get('error_571107_generic');
    }
    return errors[errorCode];
};

/**
 * Initializes the player
 * @param {StarzPlayerConfiguration} configuration
 * @param eventHandlers
 */
BitmovinAdapter.prototype.initialize = function(configuration, eventHandlers, options) {
    // Initialize the player
    this.addEventHandlers(eventHandlers, this.player);
    configure(configuration, this.player, options);
};

BitmovinAdapter.prototype.startBuffering = function() {
    setTimeout(() => {
        this.player.play();
        setTimeout(() => {
            this.player.pause();
        }, 100);
    }, 100);
};

BitmovinAdapter.prototype.removeEventHandlers = function() {
    // if(this.eventHandlers && this.eventHandlers.length > 0) {
    for (let key in this.eventHandlers) {
        if (this.eventHandlers.hasOwnProperty(key)) {
            let value = this.eventHandlers[key];
            this.player.removeEventHandler(key, value);
        }
    }
    // }
};

/**
 * @param eventHandlers
 * @param player
 */
BitmovinAdapter.prototype.addEventHandlers = function(eventHandlers) {
    this.eventHandlers = {};
    this.eventHandlers[bitmovin.player.EVENT.ON_PLAY] = eventHandlers.onPlay;
    this.eventHandlers[bitmovin.player.EVENT.ON_PAUSE] = eventHandlers.onPause;
    this.eventHandlers[bitmovin.player.EVENT.ON_SHOW_CONTROLS] = eventHandlers.onShowControls;
    this.eventHandlers[bitmovin.player.EVENT.ON_HIDE_CONTROLS] = eventHandlers.onHideControls;
    this.eventHandlers[bitmovin.player.EVENT.ON_ERROR] = eventHandlers.onError;
    this.eventHandlers[bitmovin.player.EVENT.ON_READY] = eventHandlers.onReady;
    this.eventHandlers[bitmovin.player.EVENT.ON_PLAYBACK_FINISHED] = eventHandlers.onPlaybackFinished;
    this.eventHandlers[bitmovin.player.EVENT.ON_SUBTITLE_ADDED] = eventHandlers.onSubtitleAdded;
    this.eventHandlers[bitmovin.player.EVENT.ON_SUBTITLE_CHANGE] = eventHandlers.onSubtitleChange;
    this.eventHandlers[bitmovin.player.EVENT.ON_AUDIO_CHANGE] = eventHandlers.onAudioChange;
    this.eventHandlers[bitmovin.player.EVENT.ON_SEEK] = eventHandlers.onSeek;
    this.eventHandlers[bitmovin.player.EVENT.ON_CUE_ENTER] = eventHandlers.onCueEnter;
    this.eventHandlers[bitmovin.player.EVENT.ON_CUE_EXIT] = eventHandlers.onCueExit;
    this.eventHandlers[bitmovin.player.EVENT.ON_TIME_CHANGED] = eventHandlers.onTimeChange;

    this.eventHandlers[bitmovin.player.EVENT.ON_VOLUME_CHANGE] = eventHandlers.onVolumeChange;

    this.eventHandlers[bitmovin.player.EVENT.ON_DOWNLOAD_FINISHED] = options => {
        if ((options.httpStatus === 0 || options.httpStatus > 399 || !options.success)
            && options.attempt >= options.maxAttempts) {
            console.log(JSON.stringify(options));
            ErrorHandler.throwError({
                message: LanguageManager.get('error_571121_chunks_unavailable'),
                code: 571121
            }, {
                reason: 'Bitmovin request failed',
                errorObject: options
            });
        }
    };

    for (let key in this.eventHandlers) {
        if (this.eventHandlers.hasOwnProperty(key)) {
            let value = this.eventHandlers[key];
            this.player.addEventHandler(key, value);
        }
    }

    /*
     Possible Events (6.0.0)
     ON_AD_CLICKED
     ON_AD_ERROR
     ON_AD_FINISHED
     ON_AD_LINEARITY_CHANGED
     ON_AD_MANIFEST_LOADED
     ON_AD_SCHEDULED
     ON_AD_SKIPPED
     ON_AD_STARTED
     ON_AUDIO_ADAPTATION
     ON_AUDIO_CHANGE
     ON_AUDIO_DOWNLOAD_QUALITY_CHANGE
     ON_AUDIO_PLAYBACK_QUALITY_CHANGE
     ON_CAST_AVAILABLE
     ON_CAST_LAUNCHED
     ON_CAST_PAUSE
     ON_CAST_PLAYBACK_FINISHED
     ON_CAST_PLAYING
     ON_CAST_START
     ON_CAST_STOP
     ON_CAST_TIME_UPDATE
     ON_CAST_WAITING_FOR_DEVICE
     ON_CUE_ENTER
     ON_CUE_EXIT
     ON_DOWNLOAD_FINISHED
     ON_DVR_WINDOW_EXCEEDED
     ON_ERROR
     ON_FULLSCREEN_ENTER
     ON_FULLSCREEN_EXIT
     ON_HIDE_CONTROLS
     ON_METADATA
     ON_MUTE
     ON_PAUSE
     ON_PERIOD_SWITCHED
     ON_PLAY
     ON_PLAYBACK_FINISHED
     ON_READY
     ON_SEEK
     ON_SEEKED
     ON_SEGMENT_REQUEST_FINISHED
     ON_SHOW_CONTROLS
     ON_SOURCE_LOADED
     ON_SOURCE_UNLOADED
     ON_START_BUFFERING
     ON_STOP_BUFFERING
     ON_SUBTITLE_ADDED
     ON_SUBTITLE_CHANGE
     ON_SUBTITLE_REMOVED
     ON_TIME_CHANGED
     ON_TIME_SHIFT
     ON_TIME_SHIFTED
     ON_UNMUTE
     ON_VIDEO_ADAPTATION
     ON_VIDEO_DOWNLOAD_QUALITY_CHANGE
     ON_VIDEO_PLAYBACK_QUALITY_CHANGE
     ON_VOLUME_CHANGE
     ON_VR_ERROR
     ON_VR_MODE_CHANGED
     ON_VR_STEREO_CHANGED
     ON_WARNING
     */
};

/**
 * Configures the player;
 * @param {StarzPlayerConfiguration} configuration
 * @param player
 */
function configure(configuration, player, options) {
    function parseConfiguration(config, callback) {
        drmOptions(config, function(success, drm) {
            if (success) {
                var bitmovinPlayerConfiguration = {
                    key: BITMOVIN_LICENSE_KEY,
                    playback: {
                        autoplay: false
                    },
                    skin: {
                        screenLogoImage: '' // Disable watermark
                    },
                    adaptation: {
                        desktop: {
                            startupBitrate: 1200000
                        }
                    },
                    source: {
                        dash: config.src.dash,
                        hls: config.src.hls
                    },
                    tweaks: {
                        context_menu_entries: [], // No right click button shit
                        ieXmlParserFix: true
                    },
                    location: configuration.adapter.css ? {
                        css: configuration.adapter.css,
                        ctrls_css: configuration.adapter.css
                    } : {},
                    logs: {
                        bitmovin: false // Disable the bitmovin console disclaimer
                    }
                };

                if (config.drm) {
                    bitmovinPlayerConfiguration.source['drm'] = drm;
                }

                callback(success, bitmovinPlayerConfiguration);
            } else {
                ErrorHandler.throwError(DEFAULT_ERROR, {
                    reason: 'Something went wrong while parsing DRM Options. ',
                    valueGiven: drm,
                    config: config
                });
            }
        });
    }

    parseConfiguration(configuration.asJson(), function(success, configuration) {
        Logger.debug('BitMovin configuration: ', configuration);

        if (options && options.reinitialize) {
            player.load(configuration.source);
        } else {
            player.setup(configuration, configuration.forceFlash ? 'flash.hls' : '').then(function(value) {
                // Success
                Logger.log('Successfully created bitmovin.player player element');
            }, function(reason) {
                // Error!
                Logger.error('Error while creating bitmovin.player player element');
            });
        }
    });
}

/**
 * Todo: move to Vualto
 * Dirty workaround for BitMovin, as it is not fetching the KID from the manifest itself.
 */
function getKidFromManifest(url, callback) {
    HttpUtils.get(url, function(success, message) {
        if (success && message) {
            if (message.indexOf('Access Denied') > -1) {
                ErrorHandler.throwError({
                    code: 570106,
                    message: LanguageManager.get('error_570106_invalid_country')
                }, 'KID couldnt be fetched from manifest because user is outside operating territory');
            }

            var kidReg = /cenc:default_KID="(.+?)"/g.exec(message);
            kidReg && kidReg[1] ?
                callback(true, kidReg[1]) :
                callback(true, null); // not throwing error because not getting kid for live content
        } else {
            callback(false, 'Could not fetch KID. HTTP request failed.');
        }
    });
}

/**
 * Todo: Async?
 * @param config
 * @param callback
 */
function drmOptions(config, callback) {
    var drmConfig = {};
    async.waterfall([
        function widevine(callback) {
            if (config.drm.widevine) {
                getKidFromManifest(config.src.dash, function(success, kid) {
                    drmConfig.widevine = {
                        LA_URL: config.drm.widevine.laUrl,
                        prepareMessage: function(keyMessage) {
                            return JSON.stringify({
                                token: config.drm.widevine.token,
                                drm_info: Array.apply(null, new Uint8Array(keyMessage.message)),
                                kid: kid// keyMessage.kid
                            });
                        }
                    };
                    callback(!success); // Kid can also contain error
                });
            } else {
                callback(null);
            }
        },
        function(callback) {
            if (config.drm.playready) {
                drmConfig.playready = {
                    customData: config.drm.playready.token
                    // headers: [
                    //     {
                    //         name: 'Content-Type',
                    //         value: 'application/xml'
                    //     }
                    // ]
                };
            }

            if (config.drm.fairplay) {
                var contentId;
                drmConfig.fairplay = {
                    LA_URL: config.drm.fairplay.laUrl,
                    certificateURL: config.drm.fairplay.certificateUrl,
                    certificateHeaders: [{
                        name: 'x-vudrm-token',
                        value: config.drm.fairplay.token
                    }],
                    headers: [{
                        name: 'Content-Type',
                        value: 'application/json'
                    }],
                    prepareMessage: function(keyMessage) {
                        return JSON.stringify({
                            token: config.drm.fairplay.token,
                            contentId: contentId,
                            payload: keyMessage.messageBase64Encoded
                        });
                    },
                    prepareContentId: function(rawContentId) {
                        var tmp = rawContentId.split('/');
                        contentId = tmp[tmp.length - 1];
                        return contentId;
                    },
                    prepareCertificate: function(cert) {
                        return new Uint8Array(cert);
                    },
                    prepareLicense: function(license) {
                        return new Uint8Array(license);
                    },
                    licenseResponseType: 'arraybuffer'
                };
            }
            callback(null);
        }
    ], function onDone(err, reason) {
        if (err) {
            Logger.error.apply(arguments);
        }
        callback(!err, err ? arguments : drmConfig);
    });
}

module.exports = BitmovinAdapter;



// WEBPACK FOOTER //
// ./src/adapters/bitmovin_adapter.js